import { NgTemplateOutlet, SlicePipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, inject, NgZone, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationStart } from '@angular/router';
import { FeatureToggleClient } from 'api-clients/accounting-api-client';
import { Menu, MenuItem, Section, SiteNav } from 'api-clients/applicant-api-client';
import { DataSnapshot } from 'firebase/database';
import { environment } from 'src/environments/environment';
import { ChipModule } from 'src/shared/ez-chip/chip.module';
import { FirebaseService } from 'src/shared/firebase/firebase.service';
import { materialModules } from 'src/shared/material.module';
import { MobileStateService } from 'src/shared/mobile-state.service';
import { toggleFreshchatHelp } from 'src/shared/third-party/freshchat';
import { globalWindow } from 'src/shared/window-globals';
import { NavMenuService } from '../../shared/nav-menu.service';
import { UserService } from '../../shared/user.service';
import { NavMenuAccountingService } from './nav-menu-accounting.service';

interface ComputedNavMenu extends SiteNav {
  id: number;
  menus: ComputedMenuItem[];
}

interface ComputedMenuItem extends Menu {
  id: string;
  isRouterLink: boolean;
  sections: ComputedMenuSection[];
}

interface ComputedMenuSection extends Section {
  id: string;
  menuItems: ComputedMenuItem[];
}

@Component({
  selector: 'ez-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.scss'],
  providers: [
    NavMenuAccountingService,
    { provide: FeatureToggleClient, useValue: new FeatureToggleClient() },
  ],
  imports: [
    ChipModule,
    materialModules,
    NgTemplateOutlet,
    SlicePipe,
  ],
  host: {
    '[class.mobile-menu-open]': 'mobileMenuVisible()',
    '[class.login-as]': 'loginAs()',
  },
})
export class NavMenuComponent implements AfterViewInit, OnInit {

  readonly siteNav = signal<ComputedNavMenu>({ menus: [], userFullNameWithUserName: '', id: 0 });

  readonly currentTab = signal<ComputedMenuItem | null>(null);
  readonly loginAs = signal(false);
  readonly mobileMenuVisible = signal(false);
  readonly openSideBar = signal(false);

  #firebasePrefs: FirebasePreferences;
  #isMobile: boolean;
  #suppressMoveAndClick: boolean;
  #timeoutClose: number;
  #timeoutOpen: number;

  get #isLegacyPage(): boolean {
    return !window.location.pathname.includes(environment.fiveBaseHref) && !window.location.pathname.includes(environment.internalBaseHref);
  }

  readonly #destroyRef = inject(DestroyRef);
  readonly #firebaseService = inject(FirebaseService);
  readonly #mobileState = inject(MobileStateService);
  readonly #navMenuAccountingService = inject(NavMenuAccountingService);
  readonly #navMenuService = inject(NavMenuService);
  readonly #ngZone = inject(NgZone);
  readonly #userService = inject(UserService);

  constructor() {

    this.#registerIcons();

    this.#mobileState.windowResize
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isMobile) => {
        this.#isMobile = isMobile;
        this.#closeMobileMenu();
      });


    globalWindow.interop.addRouterSub((event: Event) => {
      if (event instanceof NavigationStart && window.outerWidth <= 599) {
        this.#closeMobileMenu();
        document.body.classList.remove('mobile-menu-open');
        const navMenu = document.getElementById('Navbar');
        navMenu?.classList.remove('mobile-menu-open');

        const overlay = document.querySelector('.sidebar-overlay');
        overlay?.remove();

        const accountSidebar = document.getElementById('AccountSidebar');
        accountSidebar?.classList.remove('mobile-sidebar');
      }
    });

    this.#navMenuService.hamburgerClicked
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.toggleMobileMenu();
        this.openSideBar.set(false);
      });
  }

  ngOnInit(): void {
    // we don't want to getSiteNav on pages which have the AccountContextServiceResolver because we need to fetch
    // the getSiteNav AFTER getApplicantSidebar the resolver will trigger the updateNavMenu when it is ready
    if (!window.location.href.match(/account\/[1-9]+/)) {
      this.#loadNavMenu();
    }

    this.#userService.getUser().then(() => this.loginAs.set(!!this.#userService.user?.supportPersonId));

    this.#navMenuService.updateNavMenu
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#loadNavMenu());

    this.#navMenuService.navUpdated
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(navMenu => this.siteNav.set(navMenu as ComputedNavMenu));
  }

  ngAfterViewInit(): void {
    this.#setupFirebaseBadgeWatch();
  }

  #registerIcons(): void {

    const domSanitizer = inject(DomSanitizer);
    const matIconRegistry = inject(MatIconRegistry);

    // This is an exception to the UrlResolver format use. DO NOT COPY
    const svgBasePath = this.#isLegacyPage ? globalWindow.UrlResolver.CDN('Shared/shell-five/assets/svgs') : 'assets/svgs';
    matIconRegistry.addSvgIcon(
      'carrier_competitive_intelligence_data',
      domSanitizer.bypassSecurityTrustResourceUrl(`${svgBasePath}/carrier-competitive-intelligence-data.svg`),
    );

    matIconRegistry.addSvgIcon(
      'communication_center',
      domSanitizer.bypassSecurityTrustResourceUrl(`${svgBasePath}/communication-center.svg`),
    );

    matIconRegistry.addSvgIcon(
      'retention_center',
      domSanitizer.bypassSecurityTrustResourceUrl(`${svgBasePath}/retention-center.svg`),
    );

    matIconRegistry.addSvgIcon(
      'ez_map',
      domSanitizer.bypassSecurityTrustResourceUrl(`${svgBasePath}/ez-map.svg`),
    );
  }

  async #loadNavMenu(): Promise<void> {
    const rawMenu = await this.#navMenuService.getSiteNav(window.location.href);
    const navMenu = await this.#buildNavMenu(rawMenu);
    this.siteNav.set(navMenu);
  }

  async #buildNavMenu(navMenu: SiteNav): Promise<ComputedNavMenu> {

    await this.#buildAccountingMenuAndAccountingReportLink(navMenu);
    const menu = this.#fixMenuUrls(navMenu);
    this.#navMenuService.saveSiteNav(menu);
    return menu;
  }

  async #buildAccountingMenuAndAccountingReportLink(navMenu: SiteNav): Promise<void> {

    // check for the accounting product (and accounting menuItem) before appending the accounting menu items
    const accountingRoot = navMenu.menus.find(menuItem => menuItem.label === 'Accounting');
    const reportsRoot = navMenu.menus.find(menuItem => menuItem.label === 'Reports');
    const section = reportsRoot?.sections.find(section => section.label === 'Categories');
    const user = this.#userService.user;
    if (user.products.accounting && user.permissions.policy.accounting && accountingRoot) {
      try {
        const accountingRoles = await this.#navMenuAccountingService.getAccountingRoles();

        // Add accountingMenu to the navMenu+
        accountingRoot.sections = [{
          label: 'Accounting',
          menuItems: this.#navMenuAccountingService.buildAccountingMenu(accountingRoles),
          beta: false,
          newMenu: false,
          name: 'PolicyAccounting',
          hideOnMobile: false,
          badge: false,
          useSVG: false,
        }];
        // Add accounting report link to the navMenu if looker reports is enabled
        const acctReportMenuItem = this.#navMenuAccountingService.buildAccountingReportsLink(accountingRoles);
        if (section && acctReportMenuItem) {
          const allCategories = section.menuItems.splice(section.menuItems.findIndex(item => item.label === 'All Categories'), 1);
          section.menuItems = [acctReportMenuItem, ...section.menuItems.filter(item => item.label !== 'All Categories')];
          section.menuItems.sort((a, b) => a.label.localeCompare(b.label));
          // All Categories should be the first link always
          section.menuItems.unshift(...allCategories);
        }

      } catch (error) {
        console.error(error, 'Failed to create the accounting menu items.');
      }
    }
  }

  #fixMenuUrls(navMenu: SiteNav): ComputedNavMenu {
    const menu = <ComputedNavMenu>{
      ...navMenu,
      menus: navMenu.menus.map((menu) => {
        const menuIsRouterLink = this.#isRouterLink(menu.url ?? '');
        return <ComputedMenuItem>{
          ...menu,
          id: `root-${this.#getIdentifier(menu)}`, // Add root- to distinctify from identically named submenus
          isRouterLink: menuIsRouterLink,
          sections: menu.sections?.map((section, index) => {
            return <ComputedMenuSection>{
              ...section,
              id: `menu-section-${index}`,
              menuItems: section.menuItems?.map((item) => {
                const itemIsRouterLink = this.#isRouterLink(item.url ?? '');
                return <ComputedMenuItem>{
                  ...item,
                  id: this.#getIdentifier(item),
                  isRouterLink: itemIsRouterLink,
                };
              }),
            };
          }),
        };
      }),
    };
    return menu;
  }

  openSideMenu(tab: ComputedMenuItem): void {
    if (this.currentTab() === tab) {
      return;
    }

    window.clearTimeout(this.#timeoutOpen);

    this.#timeoutOpen = window.setTimeout(() => {
      window.clearTimeout(this.#timeoutClose);

      this.openSideBar.set(true);
      this.currentTab.set(tab);
    }, this.openSideBar() ? 50 : 0);
  }

  closeSideMenu(evt: MouseEvent): void {
    window.clearTimeout(this.#timeoutClose);
    window.clearTimeout(this.#timeoutOpen);

    const nextElement = evt.relatedTarget as Element;

    this.#timeoutClose = window.setTimeout(() => {
      this.openSideBar.set(!(evt && nextElement && !(nextElement.closest('.menu-expanded')
        || nextElement.closest('.nav-menu')) || !nextElement));

      if (!this.openSideBar()) {
        this.currentTab.set(null);
      }
    }, this.openSideBar() ? 80 : 0);
  }

  track(menuItem: MenuItem): void {
    if (menuItem.label === 'Refer a Friend' || menuItem.label === 'Reports 5.0') {
      this.hideBadge(menuItem);
    }
  }

  hideBadge(menuItem: MenuItem): void {
    menuItem.badge = false;

    if (menuItem.label === 'Refer a Friend') {
      this.#firebasePrefs.badges.referFriendHidden = true;
    } else if (menuItem.label === 'Reports 5.0') {
      this.#firebasePrefs.badges.reportsBetaHidden = true;
    }

    this.#firebaseService.saveUserNode('preferences', JSON.stringify(this.#firebasePrefs));
  }

  preventInteraction(): void {
    this.#suppressMoveAndClick = true;
  }

  handleTouch(menuItem: ComputedMenuItem, evt: TouchEvent): void {
    if (!this.#suppressMoveAndClick) {
      evt.preventDefault();
      this.handleNavClick(menuItem, evt);
    }

    this.#suppressMoveAndClick = false;
  }

  handleNavClick(menuItem: ComputedMenuItem, evt?: MouseEvent | TouchEvent): void {
    this.track(menuItem);
    if (menuItem.label === 'Agency Websites') {
      this.#navMenuService.getAgencyWebsitesUrl().then((token) => {
        window.open(`https://edit-content.com/login?token=${token}&logout-redirect=${globalWindow.UrlResolver.MrFive('dashboard')}`, '_blank');
      });
    } else if (this.openSideBar() && menuItem.label === this.currentTab()?.label) {
      this.closeSideMenu(evt as MouseEvent);
    } else {
      this.openSideMenu(menuItem);
    }
  }

  #isRouterLink(url: string): boolean {
    return url.includes(environment.fiveBaseHref)
      && window.location.pathname.includes(environment.fiveBaseHref);
  }

  interopRouterLink(event: Event, item: ComputedMenuItem): void {
    event.preventDefault();
    this.track(item);
    this.currentTab.set(null);
    const actualURL = new URL(item.url);

    if (item.targetNewWindow) {
      window.open(actualURL, '_blank');
    } else {
      globalWindow.interop.ezlynx4RouterLink(actualURL);
    }
  }

  #getIdentifier(menuItem: MenuItem): string {
    return `NavMenu_${menuItem.label.replaceAll(' ', '_')}`;
  }

  toggleMobileMenu(): void {
    this.mobileMenuVisible.update(visible => !visible);
    this.openSideBar.set(false);
    this.currentTab.set(null);
  }

  sliceRecent(section: Section): number {
    if (section.label === 'Recent Applicants' || section.label === 'Recent Quotes') {
      return this.#isMobile ? 5 : section.menuItems.length;
    }

    return section.menuItems.length;
  }

  protected toggleSupport(): void {
    toggleFreshchatHelp();
  }

  #closeMobileMenu(): void {
    this.mobileMenuVisible.set(false);
    this.openSideBar.set(false);
    this.currentTab.set(null);
  }

  #setupFirebaseBadgeWatch(): void {
    this.#firebasePrefs = {
      badges: {},
    };
    window.setTimeout(() => {
      this.#firebaseService.watchUserNode('preferences', 'value', (snapshot: DataSnapshot) => {
        if (snapshot?.val()) {
          this.#firebasePrefs = Object.assign(this.#firebasePrefs, JSON.parse(snapshot.val()));
        }

        const referFriend = this.siteNav()?.menus.find(menu => menu.label === 'Refer a Friend');

        if (referFriend) {
          this.#ngZone.run(() => referFriend.badge = !this.#firebasePrefs.badges.referFriendHidden);
        }

        const betaReports = this.siteNav()?.menus.find(menu => menu.label === 'Reports 5.0');

        if (betaReports) {
          this.#ngZone.run(() => betaReports.badge = !this.#firebasePrefs.badges.reportsBetaHidden);
        }

      });
    }, 4000);
  }

}

interface FirebasePreferences {
  badges: NavMenuBadges;
}

interface NavMenuBadges {
  referFriendHidden?: boolean;
  reportsBetaHidden?: boolean;
}
